import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const ResearchStudiesServicesPage = () => {
  const researchServices = [
    {
      icon: "BarChart3",
      title: "Études de Marché",
      description: "Analyses approfondies des tendances, opportunités et dynamiques de votre secteur pour des décisions éclairées.",
      features: ["Analyse concurrentielle", "Segmentation marché", "Prévisions sectorielles"]
    },
    {
      icon: "Users",
      title: "Enquêtes Sectorielles",
      description: "Collecte de données primaires auprès de vos cibles pour une compréhension fine de leurs besoins et comportements.",
      features: ["Questionnaires personnalisés", "Échantillonnage rigoureux", "Analyse statistique"]
    },
    {
      icon: "TrendingUp",
      title: "Analyses Socio-économiques",
      description: "Évaluation des impacts sociaux et économiques pour guider vos projets de développement et investissements.",
      features: ["Analyse d\'impact", "Études de faisabilité", "Évaluation de projets"]
    },
    {
      icon: "Eye",
      title: "Veille Stratégique",
      description: "Surveillance continue de votre environnement pour anticiper les changements et saisir les opportunités.",
      features: ["Intelligence de marché", "Monitoring concurrentiel", "Analyse de risques"]
    }
  ];

  const keyBenefits = [
    {
      icon: 'Award',
      title: 'Méthodologie Certifiée ISO',
      description: 'Processus rigoureux garantissant la fiabilité et la validité de nos analyses.'
    },
    {
      icon: 'Users',
      title: 'Experts Sectoriels',
      description: 'Chercheurs qualifiés avec expertise approfondie dans votre domaine d\'activité.'
    },
    {
      icon: 'Target',
      title: 'Insights Actionnables',
      description: 'Recommandations stratégiques directement applicables pour maximiser l\'impact.'
    },
    {
      icon: 'TrendingUp',
      title: 'Précision Prédictive',
      description: 'Analyses prédictives à 95% de précision pour sécuriser vos décisions stratégiques.'
    }
  ];

  const successStories = [
    {
      client: "Groupe Industriel Africain",
      sector: "Manufacturing",
      challenge: "Expansion régionale nécessitant une compréhension approfondie des marchés cibles",
      solution: "Étude de marché multi-pays avec analyse concurrentielle et identification d'opportunités",
      results: ["95% de précision prédictive", "4 nouveaux marchés identifiés", "ROI de 340% sur 2 ans"],
      icon: "Factory"
    },
    {
      client: "Organisation Internationale",
      sector: "Développement",
      challenge: "Évaluation d\'impact d\'un programme de formation professionnelle",
      solution: "Enquête longitudinale auprès de 3,500 bénéficiaires avec analyse socio-économique",
      results: ["85% d\'insertion professionnelle", "Amélioration revenus +45%", "Satisfaction 92%"],
      icon: "Users"
    },
    {
      client: "Entreprise Technologique",
      sector: "Tech & Innovation",
      challenge: "Lancement de nouveaux produits nécessitant une validation marché",
      solution: "Étude de faisabilité avec tests utilisateurs et analyse de positionnement",
      results: ["Validation concept à 88%", "Réduction risques -60%", "Time-to-market optimisé"],
      icon: "Rocket"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Section 1: Hero/Introduction */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Icon name="BarChart3" size={20} style={{ color: '#9B8E57' }} />
                <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                  Excellence en Recherche
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Études et Recherches
              </h1>
              
              <p className="text-xl md:text-2xl font-semibold" style={{ color: '#9B8E57' }}>
                Données Fiables pour Décisions Stratégiques
              </p>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                Le Cabinet CRF Perfection transforme vos interrogations en certitudes. Des analyses rigoureuses, 
                des données fiables et des insights actionnables pour accélérer votre croissance et sécuriser 
                vos investissements stratégiques.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{ 
                    backgroundColor: '#96121D',
                    boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#7d0f18';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#96121D';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Demander une Étude
                  <Icon name="ArrowRight" size={20} className="ml-2" />
                </Link>
                
                <Link 
                  to="/service-request-form"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all duration-300"
                  style={{ 
                    borderColor: '#9B8E57',
                    color: '#9B8E57'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#9B8E57';
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#9B8E57';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Découvrir nos Services
                  <Icon name="BookOpen" size={20} className="ml-2" />
                </Link>
              </div>
            </div>

            {/* Right Content - Stats Cards */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                       style={{ backgroundColor: 'rgba(155, 142, 87, 0.2)' }}>
                    <Icon name="FileText" size={24} style={{ color: '#9B8E57' }} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">500+</div>
                <div className="text-gray-300 text-sm">Études Réalisées</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                       style={{ backgroundColor: 'rgba(150, 18, 29, 0.2)' }}>
                    <Icon name="Award" size={24} style={{ color: '#96121D' }} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">95%</div>
                <div className="text-gray-300 text-sm">Précision Prédictive</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                       style={{ backgroundColor: 'rgba(155, 142, 87, 0.2)' }}>
                    <Icon name="Users" size={24} style={{ color: '#9B8E57' }} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">15K+</div>
                <div className="text-gray-300 text-sm">Répondants Annuels</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                       style={{ backgroundColor: 'rgba(150, 18, 29, 0.2)' }}>
                    <Icon name="CheckCircle" size={24} style={{ color: '#96121D' }} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">ISO</div>
                <div className="text-gray-300 text-sm">Qualité Certifiée</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Research Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Services de Recherche
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des solutions complètes d'études et d'analyses pour éclairer vos décisions stratégiques
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {researchServices?.map((service, index) => (
              <div 
                key={index}
                className="group bg-gray-50 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl border-2 border-transparent"
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = index % 2 === 0 ? '#96121D' : '#9B8E57';
                  e.currentTarget.style.transform = 'translateY(-8px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div 
                    className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                    }}
                  >
                    <Icon 
                      name={service?.icon} 
                      size={32} 
                      style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} 
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {service?.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service?.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {service?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <Icon 
                        name="CheckCircle" 
                        size={20} 
                        style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} 
                      />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Key Benefits & Methodology */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir Nos Études ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des avantages concrets pour maximiser la fiabilité et l'impact de vos analyses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyBenefits?.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-8 text-center transition-all duration-300 hover:shadow-xl"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ 
                    backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                  }}
                >
                  <Icon 
                    name={benefit?.icon} 
                    size={36} 
                    style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }} 
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit?.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit?.description}
                </p>
              </div>
            ))}
          </div>

          {/* Methodology & Success Stories Section */}
          <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Notre Méthodologie
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Un processus rigoureux en 4 étapes garantissant la fiabilité et la pertinence de nos analyses 
                  pour des décisions stratégiques éclairées et sécurisées.
                </p>
                <div className="flex items-start space-x-3 mb-4">
                  <Icon name="CheckCircle" size={24} style={{ color: '#96121D' }} className="flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Cadrage & Collecte</h4>
                    <p className="text-gray-600">Définition des objectifs et recueil de données rigoureuses</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={24} style={{ color: '#9B8E57' }} className="flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Analyse & Recommandations</h4>
                    <p className="text-gray-600">Traitement statistique et insights actionnables</p>
                  </div>
                </div>
              </div>

              <div 
                className="rounded-2xl p-8 text-white"
                style={{ 
                  background: 'linear-gradient(135deg, #96121D 0%, #7d0f18 100%)'
                }}
              >
                <h3 className="text-2xl font-bold mb-4">Études à Impact Mesurable</h3>
                <div className="space-y-4">
                  {successStories?.slice(0, 3)?.map((story, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <div className="flex items-center space-x-3 mb-2">
                        <Icon name={story?.icon} size={20} />
                        <h4 className="font-semibold">{story?.client}</h4>
                      </div>
                      <p className="text-sm text-gray-200">
                        {story?.results?.[0]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Transformez Vos Données en Décisions Stratégiques
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Démarrez votre étude dès aujourd'hui et sécurisez vos investissements avec des analyses fiables
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <Icon name="Phone" size={32} className="mx-auto mb-4" style={{ color: '#9B8E57' }} />
              <h3 className="text-xl font-bold mb-2">Consultation Gratuite</h3>
              <p className="text-gray-300 mb-4">Discutons de vos besoins en recherche</p>
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center text-sm font-semibold hover:underline"
                style={{ color: '#9B8E57' }}
              >
                Prendre rendez-vous <Icon name="ArrowRight" size={16} className="ml-2" />
              </Link>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <Icon name="FileText" size={32} className="mx-auto mb-4" style={{ color: '#96121D' }} />
              <h3 className="text-xl font-bold mb-2">Portefeuille d'Études</h3>
              <p className="text-gray-300 mb-4">Explorez nos réalisations</p>
              <Link 
                to="/service-request-form"
                className="inline-flex items-center justify-center text-sm font-semibold hover:underline"
                style={{ color: '#96121D' }}
              >
                Voir nos études <Icon name="Download" size={16} className="ml-2" />
              </Link>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
              <Icon name="Mail" size={32} className="mx-auto mb-4" style={{ color: '#9B8E57' }} />
              <h3 className="text-xl font-bold mb-2">Devis Personnalisé</h3>
              <p className="text-gray-300 mb-4">Recevez une proposition adaptée</p>
              <Link 
                to="/information-request-form"
                className="inline-flex items-center justify-center text-sm font-semibold hover:underline"
                style={{ color: '#9B8E57' }}
              >
                Demander un devis <Icon name="ArrowRight" size={16} className="ml-2" />
              </Link>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center px-12 py-5 text-xl font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-2xl"
              style={{ 
                backgroundColor: '#96121D',
                boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#7d0f18';
                e.target.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#96121D';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Lancer Mon Étude
              <Icon name="ArrowRight" size={24} className="ml-3" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResearchStudiesServicesPage;